/**
 * The shared UI kit. Every feature builds its screens from these primitives, which
 * map one to one onto the components in the Figma design system.
 *
 * One style for the whole product (docs/PROJECT.md, the shared style spec): controls
 * 32 / 40 / 48 px (44 on touch), radius 8 for controls, 12 for cards and menus, 16 for
 * dialogs; buttons primary, secondary, tertiary, danger, danger-tertiary and link, each with
 * rest, hover, pressed, focus, disabled and busy states; motion 120 / 200 / 300 ms.
 */
import { AlertCircle, AlertTriangle, ArrowDown, ArrowUp, ArrowUpDown, CheckCircle2, Eye, EyeOff, Info, Search, Upload, X } from "lucide-react";
import { Component, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import type {
  ButtonHTMLAttributes,
  ComponentPropsWithoutRef,
  CSSProperties,
  ElementType,
  ErrorInfo,
  InputHTMLAttributes,
  ReactNode,
  Ref,
  RefObject,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";

import { Avatar } from "./Avatar";
import s from "./ui.module.css";

const cx = (...names: Array<string | false | null | undefined>) => names.filter(Boolean).join(" ");

/** Points every ref at the same element. */
function useMergedRef<T>(own: RefObject<T | null>, outer?: Ref<T>) {
  return (node: T | null) => {
    own.current = node;
    if (typeof outer === "function") outer(node);
    else if (outer) (outer as RefObject<T | null>).current = node;
  };
}

/* ---------------------------------------------------------------- Button */
/** "ghost" is the earlier name of "tertiary" and means the same. */
export type ButtonVariant = "primary" | "secondary" | "tertiary" | "ghost" | "danger" | "danger-tertiary" | "link";
export type ButtonSize = "small" | "medium" | "large";

const variantClass: Record<ButtonVariant, string | undefined> = {
  primary: s.primary,
  secondary: s.secondary,
  tertiary: s.tertiary,
  ghost: s.tertiary,
  danger: s.danger,
  "danger-tertiary": s.dangerTertiary,
  link: s.link,
};
const sizeClass: Record<ButtonSize, string | undefined> = { small: s.small, medium: undefined, large: s.large };

function buttonClass(variant: ButtonVariant, size: ButtonSize, block?: boolean, iconOnly?: boolean) {
  return cx(s.button, variantClass[variant], sizeClass[size], block && s.block, iconOnly && s.iconOnly);
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  ref?: Ref<HTMLButtonElement>;
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  /**
   * Working on it: the button keeps its fill, its width and its label (callers say "Saving…"),
   * shows a spinner in place of its icon and ignores presses. Not the disabled look.
   */
  busy?: boolean;
  iconOnly?: boolean;
};

export function Button({ ref, variant = "secondary", size = "medium", block, busy, iconOnly, className, children, disabled, type = "button", onClick, ...rest }: ButtonProps) {
  const own = useRef<HTMLButtonElement>(null);
  const setRef = useMergedRef(own, ref);
  const restWidth = useRef(0);
  // A busy button is at least as wide as it was at rest, so "Save changes" → "Saving…" does not jump.
  useLayoutEffect(() => {
    const el = own.current;
    if (!el) return;
    if (busy) {
      if (restWidth.current > 0) el.style.minWidth = `${restWidth.current}px`;
    } else {
      el.style.minWidth = "";
      restWidth.current = el.getBoundingClientRect().width;
    }
  }, [busy]);

  return (
    <button
      ref={setRef}
      type={type}
      className={cx(buttonClass(variant, size, block, iconOnly), className)}
      // Busy ignores presses but is not disabled: a disabled button drops the keyboard focus to the page, so
      // after a failed save the person would have to find their place again.
      disabled={disabled}
      aria-disabled={(busy && !disabled) || undefined}
      aria-busy={busy || undefined}
      onClick={busy ? (event) => event.preventDefault() : onClick}
      {...rest}
    >
      {busy ? <span className={s.buttonSpinner} aria-hidden data-spinner /> : null}
      {children}
    </button>
  );
}

type LinkButtonProps<C extends ElementType> = {
  /** The element or component to render, such as react-router's Link. An <a> by default. */
  as?: C;
  variant?: Exclude<ButtonVariant, "danger" | "danger-tertiary">;
  size?: ButtonSize;
  block?: boolean;
  className?: string;
  children?: ReactNode;
} & Omit<ComponentPropsWithoutRef<C>, "as" | "className" | "children">;

/**
 * A link that looks like a button, for going somewhere or downloading a file (a button does
 * something here). Pass `as={Link}` for a route inside the app; the kit does not import the router.
 */
export function LinkButton<C extends ElementType = "a">({ as, variant = "secondary", size = "medium", block, className, children, ...rest }: LinkButtonProps<C>) {
  const Element: ElementType = as ?? "a";
  return (
    <Element className={cx(buttonClass(variant, size, block), s.linkButton, className)} {...rest}>
      {children}
    </Element>
  );
}

type IconButtonProps = Omit<ButtonProps, "iconOnly" | "aria-label" | "title"> & {
  /** The button's name, read out and shown as a tooltip on hover and keyboard focus. */
  label: string;
  /** Where the tooltip appears; false for none (when the same words are on screen already). */
  tooltip?: "top" | "bottom" | "left" | false;
};

/** Whether a button's tooltip is on screen: the pointer is on it or it has keyboard focus. */
function tooltipShown(el: HTMLElement): boolean {
  try {
    return el.matches(":hover, :focus-visible");
  } catch {
    return document.activeElement === el;
  }
}

/**
 * A square button with only an icon. Its name comes from `label`, never from the picture.
 * The tooltip follows WCAG 1.4.13: Escape hides it without moving the pointer or focus, the
 * pointer can move onto it without it going away, and it stays until the pointer or focus leaves.
 */
export function IconButton({ ref, label, tooltip = "top", variant = "tertiary", size = "medium", className, children, onClick, onBlur, onPointerLeave, ...rest }: IconButtonProps) {
  const own = useRef<HTMLButtonElement>(null);
  const setRef = useMergedRef(own, ref);
  const [tipHidden, setTipHidden] = useState(false);
  useEffect(() => {
    if (!tooltip) return;
    const onKey = (event: KeyboardEvent) => {
      const el = own.current;
      if (event.key !== "Escape" || !el) return;
      if (tooltipShown(el)) setTipHidden(true);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [tooltip]);

  return (
    <Button
      ref={setRef}
      variant={variant}
      size={size}
      iconOnly
      aria-label={label}
      className={cx(s.iconButton, className)}
      data-tooltip-hidden={tipHidden || undefined}
      onClick={(event) => {
        // A press on the tooltip itself (it is inside the button) does nothing.
        if ((event.target as HTMLElement).closest("[data-tooltip]")) return;
        onClick?.(event);
      }}
      onBlur={(event) => {
        setTipHidden(false);
        onBlur?.(event);
      }}
      onPointerLeave={(event) => {
        setTipHidden(false);
        onPointerLeave?.(event);
      }}
      {...rest}
    >
      {children}
      {tooltip ? <span className={cx(s.tooltip, s[`tooltip-${tooltip}`])} data-tooltip aria-hidden>{label}</span> : null}
    </Button>
  );
}

/** "Skip to content": hidden until it has keyboard focus, then the first thing on the screen. */
export function SkipLink({ href = "#main", children = "Skip to content" }: { href?: string; children?: ReactNode }) {
  return <a className={s.skipLink} href={href}>{children}</a>;
}

/* ----------------------------------------------------------------- Field */
// Fields follow WCAG 2.2 and the HTML standard: a visible label tied to the control,
// every field required unless its label says "(optional)", the hint kept in view and
// still read out when an error appears, and the error announced and linked to the field.
type FieldShell = { label: string; hint?: string; error?: string; optional?: boolean };

function FieldError({ id, error }: { id: string; error: string }) {
  return (
    <span className={s.error} id={`${id}-error`} role="alert">
      <AlertCircle aria-hidden className={s.errorIcon} />
      <span className="sr-only">Error:</span>{" "}{error}
    </span>
  );
}

function FieldFrame({ id, label, hint, error, optional, children }: FieldShell & { id: string; children: ReactNode }) {
  return (
    <div className={s.field}>
      <label className={s.label} htmlFor={id}>
        {label}
        {optional ? <span className={s.optional}> (optional)</span> : null}
      </label>
      {children}
      {hint ? <span className={s.hint} id={`${id}-hint`}>{hint}</span> : null}
      {error ? <FieldError id={id} error={error} /> : null}
    </div>
  );
}

/** The ids that describe a field, hint first, for aria-describedby. */
function describedBy(id: string, hint?: string, error?: string): string | undefined {
  return [hint ? `${id}-hint` : "", error ? `${id}-error` : ""].filter(Boolean).join(" ") || undefined;
}

/** Inputs that hold an email address or a password are never auto-corrected or capitalised. */
function literal(type: InputHTMLAttributes<HTMLInputElement>["type"]): InputHTMLAttributes<HTMLInputElement> {
  if (type === "email") return { inputMode: "email", autoCapitalize: "none", autoCorrect: "off", spellCheck: false };
  if (type === "password") return { autoCapitalize: "none", autoCorrect: "off", spellCheck: false };
  return {};
}

export function TextField({ label, hint, error, optional, className, type, ...rest }: FieldShell & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  return (
    <FieldFrame id={id} label={label} hint={hint} error={error} optional={optional}>
      <input
        id={id}
        type={type}
        className={cx(s.input, error && s.invalid, className)}
        aria-required={optional ? undefined : true}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, hint, error)}
        {...literal(type)}
        {...rest}
      />
    </FieldFrame>
  );
}

/** A password field with a button that shows the password while pressed in (WCAG 2.2, 3.3.8). */
export function PasswordField({ label, hint, error, optional, className, ...rest }: FieldShell & Omit<InputHTMLAttributes<HTMLInputElement>, "type">) {
  const id = useId();
  const [shown, setShown] = useState(false);
  return (
    <FieldFrame id={id} label={label} hint={hint} error={error} optional={optional}>
      <div className={s.passwordRow}>
        <input
          id={id}
          type={shown ? "text" : "password"}
          className={cx(s.input, s.passwordInput, error && s.invalid, className)}
          aria-required={optional ? undefined : true}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(id, hint, error)}
          {...literal("password")}
          {...rest}
        />
        <button type="button" className={s.reveal} aria-controls={id} aria-pressed={shown} onClick={() => setShown((v) => !v)}>
          {shown ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
          <span className="sr-only">Show password</span>
        </button>
      </div>
    </FieldFrame>
  );
}

export function SelectField({ label, hint, error, optional, className, children, ...rest }: FieldShell & SelectHTMLAttributes<HTMLSelectElement>) {
  const id = useId();
  return (
    <FieldFrame id={id} label={label} hint={hint} error={error} optional={optional}>
      {/* The wrapper draws the chevron in a token colour; the label stays tied to the select itself. */}
      <span className={s.selectWrap}>
        <select
          id={id}
          className={cx(s.select, error && s.invalid, className)}
          aria-required={optional ? undefined : true}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(id, hint, error)}
          {...rest}
        >
          {children}
        </select>
      </span>
    </FieldFrame>
  );
}

/** A multi-line text field, for a description. Same frame, label, hint and error as TextField. */
export function TextAreaField({ label, hint, error, optional, className, ...rest }: FieldShell & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const id = useId();
  return (
    <FieldFrame id={id} label={label} hint={hint} error={error} optional={optional}>
      <textarea
        id={id}
        className={cx(s.input, s.textarea, error && s.invalid, className)}
        aria-required={optional ? undefined : true}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, hint, error)}
        {...rest}
      />
    </FieldFrame>
  );
}

/**
 * A small select inside a table row that saves as soon as it changes. Named by `label`
 * (there is no room for a visible one); while saving it keeps the chosen value and says
 * "Saving…" beside it.
 */
export function InlineSelect({ label, busy, disabled, className, children, ...rest }: { label: string; busy?: boolean } & Omit<SelectHTMLAttributes<HTMLSelectElement>, "aria-label">) {
  return (
    <span className={cx(s.inlineSelect, className)}>
      <span className={s.selectWrap}>
        <select className={cx(s.select, s.selectSmall)} aria-label={label} aria-busy={busy || undefined} disabled={disabled || busy} {...rest}>
          {children}
        </select>
      </span>
      {busy ? <SavingNote /> : null}
    </span>
  );
}

/** "Saving…" with a small spinner, beside a control that saves on change. */
function SavingNote() {
  return (
    <span className={s.saving}>
      <span className={s.savingSpinner} aria-hidden />
      Saving…
    </span>
  );
}

/* --------------------------------------------------------------- Checkbox */
/**
 * A checkbox with its label as one 44 px target. Pass `aria-label` when the visible label is
 * shorter than the name needs to be (a "Pilot" column: "Pilot: LAPTOP-RAVI"). While `busy`
 * it is not pressable and says "Saving…".
 */
export function Checkbox({ label, hint, busy, disabled, className, ...rest }: { label: ReactNode; hint?: string; busy?: boolean } & Omit<InputHTMLAttributes<HTMLInputElement>, "type">) {
  const id = useId();
  return (
    <label className={cx(s.checkField, (disabled || busy) && s.checkFieldDisabled, className)}>
      <input
        type="checkbox"
        className={s.checkbox}
        disabled={disabled || busy}
        aria-busy={busy || undefined}
        aria-describedby={hint ? `${id}-hint` : undefined}
        {...rest}
      />
      <span className={s.checkText}>
        <span className={s.checkLabel}>{label}</span>
        {hint ? <span className={s.checkSub} id={`${id}-hint`}>{hint}</span> : null}
      </span>
      {busy ? <SavingNote /> : null}
    </label>
  );
}

/* ------------------------------------------------------------- File field */
/**
 * A file picker whose visible label is the button people press. The native input stays
 * in the tab order (visually hidden), so the keyboard and screen readers get the real
 * control, with the hint and any error read with it.
 */
export function FileField({ label, hint, error, accept, onFile, inputRef }: { label: string; hint?: string; error?: string; accept: string; onFile: (file: File | null) => void; inputRef?: Ref<HTMLInputElement> }) {
  const id = useId();
  return (
    <div className={s.field}>
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        className={cx("sr-only", s.fileInput)}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, hint, error)}
        onChange={(event) => onFile(event.target.files?.[0] ?? null)}
      />
      <label htmlFor={id} className={cx(buttonClass("secondary", "medium"), s.fileButton)}>
        <Upload aria-hidden />
        {label}
      </label>
      {hint ? <span className={s.hint} id={`${id}-hint`}>{hint}</span> : null}
      {error ? <FieldError id={id} error={error} /> : null}
    </div>
  );
}

/* ------------------------------------------------------------ Radio group */
export type RadioOption<T extends string> = { value: T; label: string; description?: string; disabled?: boolean; tag?: string };

/**
 * Choose one of a few options, each with a line of explanation. A fieldset named by its
 * legend (WCAG 1.3.1), native radio buttons (arrow keys move between them), and the same
 * hint and error rules as the other fields. An option that cannot be chosen yet stays in
 * view, disabled, with its tag ("Coming soon") read as part of its name. `disabled` makes
 * the whole group read-only (people who may see the choice but not change it).
 */
export function RadioGroupField<T extends string>({ label, name, value, onChange, options, hint, error, disabled }: { label: string; name: string; value: T; onChange: (next: T) => void; options: readonly RadioOption<T>[]; hint?: string; error?: string; disabled?: boolean }) {
  const id = useId();
  return (
    <fieldset className={s.radioGroup} role="radiogroup" aria-labelledby={`${id}-legend`} aria-required aria-describedby={describedBy(id, hint, error)} disabled={disabled}>
      <legend className={s.label} id={`${id}-legend`}>{label}</legend>
      <div className={s.radioList}>
        {options.map((option) => {
          const optionId = `${id}-${option.value}`;
          const checked = option.value === value;
          const off = disabled || option.disabled;
          return (
            <label key={option.value} className={cx(s.radioRow, checked && s.radioChecked, off && s.radioDisabled, error && s.invalid)}>
              <input
                type="radio"
                className={s.radio}
                name={name}
                value={option.value}
                checked={checked}
                disabled={option.disabled}
                onChange={() => onChange(option.value)}
                aria-labelledby={`${optionId}-name`}
                aria-describedby={option.description ? `${optionId}-description` : undefined}
                aria-invalid={error ? true : undefined}
              />
              <span className={s.radioText}>
                <span className={s.radioName} id={`${optionId}-name`}>
                  {option.label}
                  {option.tag ? <>{" "}<span className={s.radioTag}>{option.tag}</span></> : null}
                </span>
                {option.description ? <span className={s.radioDescription} id={`${optionId}-description`}>{option.description}</span> : null}
              </span>
            </label>
          );
        })}
      </div>
      {hint ? <span className={s.hint} id={`${id}-hint`}>{hint}</span> : null}
      {error ? <FieldError id={id} error={error} /> : null}
    </fieldset>
  );
}

/* ---------------------------------------------------------------- Switch */
/**
 * An on/off setting that applies at once. A button with role="switch", so it reads as one.
 * While `busy` (saving) its hint line says "Saving…"; the hint is still read with the switch.
 */
export function Switch({ label, hint, checked, onChange, disabled, busy }: { label: string; hint?: string; checked: boolean; onChange: (next: boolean) => void; disabled?: boolean; busy?: boolean }) {
  const id = useId();
  return (
    <div className={s.switchRow}>
      <div className={s.switchText}>
        <span className={s.switchLabel} id={`${id}-label`}>{label}</span>
        {hint ? <span className={busy ? "sr-only" : s.hint} id={`${id}-hint`}>{hint}</span> : null}
        {busy ? <span className={s.hint} aria-hidden><SavingNote /></span> : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={`${id}-label`}
        aria-describedby={hint ? `${id}-hint` : undefined}
        aria-busy={busy || undefined}
        className={s.switch}
        disabled={disabled || busy}
        onClick={() => onChange(!checked)}
      >
        <span className={s.switchThumb} aria-hidden />
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ Card */
export function Card({ title, description, action, flush, children, className, as: Element = "section" }: { title?: string; description?: ReactNode; action?: ReactNode; flush?: boolean; children: ReactNode; className?: string; as?: "section" | "div" | "article" }) {
  return (
    <Element className={cx(s.card, className)}>
      {title ? (
        <header className={s.cardHeader}>
          <div className={s.cardHeading}>
            <h2 className={s.cardTitle}>{title}</h2>
            {description ? <p className={s.cardDescription}>{description}</p> : null}
          </div>
          {action ? <div className={s.cardAction}>{action}</div> : null}
        </header>
      ) : null}
      <div className={flush ? s.cardFlush : s.cardBody}>{children}</div>
    </Element>
  );
}

/* ----------------------------------------------------------------- Badge */
export type Tone = "ok" | "warn" | "danger" | "info" | "violet" | "neutral";
const toneClass: Record<Tone, string | undefined> = {
  ok: s.toneOk, warn: s.toneWarn, danger: s.toneDanger, info: s.toneInfo, violet: s.toneViolet, neutral: s.toneNeutral,
};

export function Badge({ tone = "neutral", dot, size = "medium", children }: { tone?: Tone; dot?: boolean; size?: "small" | "medium"; children: ReactNode }) {
  return (
    <span className={cx(s.badge, size === "small" && s.badgeSmall, toneClass[tone])}>
      {dot ? <span className={s.badgeDot} aria-hidden /> : null}
      {children}
    </span>
  );
}

/* ---------------------------------------------------------------- Person */
/**
 * A person's picture (or initials), name and a second line. Pass userId and pictureVersion to
 * show their picture. With `onOpen` the name is a button (named by `openLabel`, such as "Edit
 * Asha Rao") that opens the person; `badge` sits after the name; `muted` greys the text, for
 * someone whose sign-in is turned off.
 */
export function Person({ name, sub, userId, pictureVersion, onOpen, openLabel, badge, muted }: { name: string; sub?: string; userId?: string; pictureVersion?: string; onOpen?: () => void; openLabel?: string; badge?: ReactNode; muted?: boolean }) {
  return (
    <div className={cx(s.person, muted && s.personMuted)}>
      <Avatar name={name} userId={userId} pictureVersion={pictureVersion} />
      <div className={s.personText}>
        <div className={s.personName}>
          {onOpen ? <button type="button" className={s.personButton} aria-label={openLabel ?? name} onClick={onOpen}>{name}</button> : name}
          {badge ? <span className={s.personBadge}>{badge}</span> : null}
        </div>
        {sub ? <div className={s.personSub}>{sub}</div> : null}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- Skeleton */
/**
 * Grey shapes where content is about to appear, so the layout does not jump when it does.
 * They pulse gently (static with reduced motion). The region says "Loading…" once to screen
 * readers and is aria-busy; there is no role="status", which belongs to the save messages.
 */
export function Skeleton({ width, height = 14, radius, className }: { width?: number | string; height?: number | string; radius?: string; className?: string }) {
  return <span className={cx(s.skeleton, className)} style={{ width, height, borderRadius: radius }} aria-hidden />;
}

function SkeletonRegion({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx(s.skeletonRegion, className)} aria-busy="true">
      <span className="sr-only">Loading…</span>
      {children}
    </div>
  );
}

const LINE_WIDTHS = ["92%", "76%", "84%", "60%", "70%"];

export function SkeletonText({ lines = 3 }: { lines?: number }) {
  return (
    <SkeletonRegion className={s.skeletonText}>
      {Array.from({ length: lines }, (_, i) => <Skeleton key={i} width={LINE_WIDTHS[i % LINE_WIDTHS.length]} />)}
    </SkeletonRegion>
  );
}

/** Rows of a table about to load: a person-like first column and short cells after it. */
export function SkeletonTable({ rows = 5, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <SkeletonRegion className={s.skeletonTable}>
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className={s.skeletonRow} style={{ "--columns": columns } as CSSProperties}>
          <span className={s.skeletonPerson}>
            <Skeleton width={32} height={32} radius="50%" />
            <span className={s.skeletonLines}><Skeleton width="70%" /><Skeleton width="45%" height={10} /></span>
          </span>
          {Array.from({ length: columns - 1 }, (_, c) => <Skeleton key={c} width={`${50 + ((r + c) % 3) * 15}%`} />)}
        </div>
      ))}
    </SkeletonRegion>
  );
}

/** Seven bars of a chart about to load. */
export function SkeletonChart() {
  const heights = [55, 72, 40, 86, 64, 30, 48];
  return (
    <SkeletonRegion className={s.skeletonChart}>
      {heights.map((h, i) => <Skeleton key={i} height={`${h}%`} radius="var(--radius-sm) var(--radius-sm) 0 0" />)}
    </SkeletonRegion>
  );
}

/* ----------------------------------------------------------------- Table */
export type Column<T> = {
  /** The heading; "" for an actions column, which screen readers hear as "Actions". */
  header: ReactNode;
  /** The key, when the header is not a plain unique string. */
  id?: string;
  cell: (row: T) => ReactNode;
  align?: "right";
  width?: string;
  /** The column that names the row (a computer, an organisation): set in weight 500. */
  primary?: boolean;
  /** Stays in view at the right edge while the table scrolls sideways (an actions column). */
  sticky?: "right";
  /** Hidden on screens narrower than this many pixels, so the rest fits without scrolling. */
  hideBelow?: 1280 | 1440;
  /**
   * Makes the column sortable: its header becomes a button. The value to sort by, a number or text; `undefined`
   * (nothing to compare, such as no time at all) always sorts last, whichever way.
   */
  sortValue?: (row: T) => number | string | undefined;
  /** Which way a first press sorts: numbers usually most first ("descending"), names A to Z (the default). */
  sortFirst?: SortDirection;
};

export type SortDirection = "ascending" | "descending";
/** Which column a table is sorted by (its key: `id`, or the header text) and which way. */
export type TableSort = { column: string; direction: SortDirection };

function columnKey<T>(column: Column<T>, index: number): string {
  return column.id ?? (typeof column.header === "string" ? column.header : String(index));
}

/** The rows in the order a sort asks for. Equal values keep the order the rows came in; missing values go last. */
export function sortRows<T>(rows: readonly T[], value: (row: T) => number | string | undefined, direction: SortDirection): T[] {
  const sign = direction === "ascending" ? 1 : -1;
  const keyed = rows.map((row, index) => ({ row, index, value: value(row) }));
  keyed.sort((a, b) => {
    if (a.value === undefined || b.value === undefined) {
      if (a.value === b.value) return a.index - b.index;
      return a.value === undefined ? 1 : -1;
    }
    const order = typeof a.value === "number" && typeof b.value === "number" ? a.value - b.value : String(a.value).localeCompare(String(b.value));
    return order * sign || a.index - b.index;
  });
  return keyed.map((k) => k.row);
}

/** Whether an element is wider than its box, kept up to date as either changes. */
function useOverflows(ref: RefObject<HTMLElement | null>): boolean {
  const [overflows, setOverflows] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setOverflows(el.scrollWidth > el.clientWidth + 1);
    check();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(check);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, [ref]);
  return overflows;
}

/** True when a click landed on something in the row that does its own thing (a select, a button, a link). */
function onControl(target: EventTarget | null, row: HTMLElement): boolean {
  const control = target instanceof Element ? target.closest("a, button, input, select, textarea, label, [role='switch'], [role='button']") : null;
  return control !== null && row.contains(control);
}

/**
 * A table of rows. With `onRowClick`, a click anywhere on a row opens it (the pointer shows it), except on the
 * controls inside the row, and never when the click ends a text selection. A mouse convenience only: the row must
 * also hold a real button or link that opens it, which is what keyboards and screen readers use.
 */
export function DataTable<T>({ columns, rows, rowKey, caption, footer, onRowClick, defaultSort }: { columns: Column<T>[]; rows: T[]; rowKey: (row: T) => string; caption: string; footer?: ReactNode; onRowClick?: (row: T) => void; defaultSort?: TableSort }) {
  const wrap = useRef<HTMLDivElement>(null);
  const overflows = useOverflows(wrap);
  // Sortable columns (those with `sortValue`): pressing a header sorts by it, pressing it again turns the order round.
  const [sort, setSort] = useState<TableSort | undefined>(defaultSort);
  const sortColumn = sort ? columns.find((column, i) => columnKey(column, i) === sort.column && column.sortValue) : undefined;
  const shown = useMemo(
    () => (sort && sortColumn?.sortValue ? sortRows(rows, sortColumn.sortValue, sort.direction) : rows),
    [rows, sort, sortColumn],
  );
  const sortBy = (column: Column<T>, key: string) =>
    setSort((current) =>
      current?.column === key
        ? { column: key, direction: current.direction === "ascending" ? "descending" : "ascending" }
        : { column: key, direction: column.sortFirst ?? "ascending" },
    );
  return (
    <>
      {/*
        On narrow screens the table scrolls sideways, with a shadow at the edge that has more. When it
        does, the named region is focusable so keyboard users can scroll it too (WCAG 2.1.1).
      */}
      <div ref={wrap} className={s.tableWrap} data-overflows={overflows || undefined} role="region" aria-label={caption} tabIndex={overflows ? 0 : undefined}>
        <table className={s.table}>
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr>
              {columns.map((column, i) => {
                const key = columnKey(column, i);
                const direction = sort?.column === key && column.sortValue ? sort.direction : undefined;
                const Arrow = direction === "ascending" ? ArrowUp : direction === "descending" ? ArrowDown : ArrowUpDown;
                return (
                  <th
                    key={key}
                    scope="col"
                    className={column.align === "right" ? s.alignRight : undefined}
                    style={column.width ? { width: column.width } : undefined}
                    data-sticky={column.sticky}
                    data-hide-below={column.hideBelow}
                    aria-sort={direction}
                  >
                    {column.sortValue ? (
                      <button type="button" className={cx(s.sortButton, column.align === "right" && s.sortButtonRight)} data-sorted={direction ? true : undefined} onClick={() => sortBy(column, key)}>
                        {column.header}
                        <Arrow aria-hidden className={s.sortIcon} />
                      </button>
                    ) : (
                      column.header || <span className="sr-only">Actions</span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {shown.map((row) => (
              <tr
                key={rowKey(row)}
                className={onRowClick ? s.clickableRow : undefined}
                onClick={
                  onRowClick
                    ? (event) => {
                        if (onControl(event.target, event.currentTarget)) return;
                        if (window.getSelection?.()?.toString()) return;
                        onRowClick(row);
                      }
                    : undefined
                }
              >
                {columns.map((column, i) => (
                  <td key={columnKey(column, i)} className={cx(column.align === "right" && s.alignRight, column.primary && s.primaryCell)} data-sticky={column.sticky} data-hide-below={column.hideBelow}>
                    {column.cell(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {footer ? <div className={s.tableFooter}>{footer}</div> : null}
    </>
  );
}

/* ------------------------------------------------------------- Stat tile */
export function Tiles({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx(s.tiles, className)}>{children}</div>;
}

/** A number with its label. While `loading` the value is a grey bar, never a dash that reads as "none". */
export function StatTile({ label, value, hint, icon, loading }: { label: string; value: ReactNode; hint?: string; icon?: ReactNode; loading?: boolean }) {
  return (
    <div className={cx(s.card, s.tile)} aria-busy={loading || undefined}>
      <div className={s.tileLabel}>{icon ? <span className={s.tileIcon} aria-hidden>{icon}</span> : null}{label}</div>
      {loading ? (
        <div className={s.tileValue}><Skeleton width={80} height={28} /><span className="sr-only">Loading…</span></div>
      ) : (
        <div className={cx(s.tileValue, "num")}>{value}</div>
      )}
      {hint ? <div className={s.tileHint}>{hint}</div> : null}
    </div>
  );
}

/* ----------------------------------------------------------- Page header */
/**
 * A page's title, a line under it and its actions. `leading` stands before the title (a person's picture);
 * `meta` sits under the subtitle for what is not text (a status badge).
 */
export function PageHeader({ title, subtitle, actions, leading, meta }: { title: string; subtitle?: ReactNode; actions?: ReactNode; leading?: ReactNode; meta?: ReactNode }) {
  const text = (
    <>
      <h1 className={s.pageTitle}>{title}</h1>
      {subtitle ? <p className={s.pageSubtitle}>{subtitle}</p> : null}
      {meta ? <div className={s.pageMeta}>{meta}</div> : null}
    </>
  );
  return (
    <div className={s.pageHeader}>
      {leading ? (
        <div className={cx(s.pageHeading, s.pageHeadingLead)}>
          <div className={s.pageLeading}>{leading}</div>
          <div className={s.pageHeadingText}>{text}</div>
        </div>
      ) : (
        <div className={s.pageHeading}>{text}</div>
      )}
      {actions ? <div className={s.pageActions}>{actions}</div> : null}
    </div>
  );
}

/* ---------------------------------------------------- Empty and loading */
export function EmptyState({ icon, title, text, action, compact, heading }: { icon: ReactNode; title: string; text?: string; action?: ReactNode; compact?: boolean; heading?: "h1" | "h2" | "h3" }) {
  const Title = heading ?? "div";
  return (
    <div className={cx(s.empty, compact && s.emptyCompact)}>
      <div className={s.emptyIcon} aria-hidden>{icon}</div>
      <Title className={s.emptyTitle}>{title}</Title>
      {text ? <p className={s.emptyText}>{text}</p> : null}
      {action ? <div className={s.emptyAction}>{action}</div> : null}
    </div>
  );
}

export function Spinner({ label = "Loading" }: { label?: string }) {
  return <span className={s.spinner} role="status" aria-label={label} />;
}

export function Loading({ fullscreen }: { fullscreen?: boolean }) {
  return <div className={cx(s.centered, fullscreen && s.fullscreen)}><Spinner /></div>;
}

/**
 * A whole screen that could not be shown: a page that failed to load, an address that leads
 * nowhere. Its title is the page's heading.
 */
export function ErrorScreen({ title, text, action, fullscreen }: { title: string; text?: string; action?: ReactNode; fullscreen?: boolean }) {
  return (
    <div className={cx(s.errorScreen, fullscreen && s.fullscreen)}>
      <EmptyState icon={<AlertTriangle />} title={title} text={text} action={action} heading="h1" />
    </div>
  );
}

/**
 * Catches a screen that throws while rendering, or a part of the app that fails to download
 * after an update, and shows a way out instead of a blank page. `resetKey` (the address) clears
 * the error when people go somewhere else.
 */
export class ErrorBoundary extends Component<{ children: ReactNode; resetKey?: string; fullscreen?: boolean }, { failed: boolean; resetKey?: string }> {
  state: { failed: boolean; resetKey?: string } = { failed: false, resetKey: this.props.resetKey };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  static getDerivedStateFromProps(props: { resetKey?: string }, state: { failed: boolean; resetKey?: string }) {
    return props.resetKey !== state.resetKey ? { failed: false, resetKey: props.resetKey } : null;
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error("A screen failed to show", error, info.componentStack);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <ErrorScreen
        fullscreen={this.props.fullscreen}
        title="This page didn't load"
        text="Zotula may have been updated while it was open. Reload to get the latest version; nothing you saved is lost."
        action={<Button variant="primary" onClick={() => window.location.reload()}>Reload</Button>}
      />
    );
  }
}

/* ---------------------------------------------------------------- Banner */
type BannerTone = "danger" | "ok" | "info" | "warn";

export function Banner({ tone = "danger", title, action, onDismiss, children }: { tone?: BannerTone; title?: string; action?: ReactNode; onDismiss?: () => void; children: ReactNode }) {
  const Icon = tone === "ok" ? CheckCircle2 : tone === "info" ? Info : AlertCircle;
  return (
    <div className={cx(s.banner, toneClass[tone])} role={tone === "danger" ? "alert" : "status"}>
      <Icon aria-hidden className={s.bannerIcon} />
      <div className={s.bannerBody}>
        {title ? <p className={s.bannerTitle}>{title}</p> : null}
        <div>{children}</div>
      </div>
      {action ? <div className={s.bannerAction}>{action}</div> : null}
      {onDismiss ? <IconButton label="Dismiss" size="small" tooltip="left" className={s.bannerDismiss} onClick={onDismiss}><X aria-hidden /></IconButton> : null}
    </div>
  );
}

/* ----------------------------------------------------------- Form actions */
/**
 * The save row under a form: the primary button ("Saving…" while it works), Discard once
 * something changed, and "Saved." beside the button after a save (read out once). `sticky`
 * keeps it at the bottom of the screen on long forms.
 */
export function FormActions({ submitLabel, savingLabel = "Saving…", saving, dirty, saved, onDiscard, sticky, children }: { submitLabel: string; savingLabel?: string; saving: boolean; dirty: boolean; saved: boolean; onDiscard?: () => void; sticky?: boolean; children?: ReactNode }) {
  return (
    <div className={cx(s.formActions, sticky && s.formActionsSticky)}>
      <Button type="submit" variant="primary" busy={saving} disabled={!dirty}>{saving ? savingLabel : submitLabel}</Button>
      {dirty && onDiscard ? <Button onClick={onDiscard}>Discard</Button> : null}
      {/* The live region is always there, so screen readers announce "Saved." when it fills in. */}
      <span className={s.saved} role="status">{saved && !dirty ? <><CheckCircle2 aria-hidden />Saved.</> : null}</span>
      {children}
    </div>
  );
}

/* ---------------------------------------------------------------- Dialog */
/**
 * A modal built on the native <dialog>: focus trap, Escape and backdrop come from the browser.
 * The header and the footer stay in view while a long body scrolls; on phones it rises from
 * the bottom as a sheet. Sizes: small 400, medium 480, large 640 px.
 */
export function Dialog({ open, title, onClose, children, footer, onSubmit, size = "medium" }: { open: boolean; title: string; onClose: () => void; children: ReactNode; footer: ReactNode; onSubmit?: () => void; size?: "small" | "medium" | "large" }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog ref={ref} className={cx(s.dialog, size === "small" && s.dialogSmall, size === "large" && s.dialogLarge)} aria-labelledby={titleId} onClose={onClose} onCancel={onClose}>
      {open ? (
        <form
          className={s.dialogForm}
          method="dialog"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit?.();
          }}
        >
          <header className={s.dialogHeader}>
            <h2 className={s.dialogTitle} id={titleId}>{title}</h2>
            <IconButton label="Close" tooltip="bottom" onClick={onClose}><X aria-hidden /></IconButton>
          </header>
          <div className={s.dialogBody}>{children}</div>
          <footer className={s.dialogFooter}>{footer}</footer>
        </form>
      ) : null}
    </dialog>
  );
}

/* ------------------------------------------------------------- Checklist */
export type CheckItem = { id: string; label: string; sub?: string };

/** Lists longer than this get a filter box and "Select all" / "Clear". */
const LONG_LIST = 8;

/**
 * Pick several items from a list, with a filter box once the list is long. The filter has
 * its own visible label; its placeholder is an example of what to type (filterExample). An
 * `error` is announced and tied to the group like any other field's.
 */
export function CheckList({ label, items, selected, onChange, emptyText, hint, error, optional, filterExample }: { label: string; items: CheckItem[]; selected: ReadonlySet<string>; onChange: (next: Set<string>) => void; emptyText: string; hint?: string; error?: string; optional?: boolean; filterExample: string }) {
  const id = useId();
  const [filter, setFilter] = useState("");
  const needle = filter.trim().toLowerCase();
  const shown = needle ? items.filter((item) => `${item.label} ${item.sub ?? ""}`.toLowerCase().includes(needle)) : items;
  const long = items.length > LONG_LIST;
  const unchosen = shown.filter((item) => !selected.has(item.id)).length;

  function toggle(itemId: string) {
    const next = new Set(selected);
    if (!next.delete(itemId)) next.add(itemId);
    onChange(next);
  }

  return (
    <div className={s.checklist} role="group" aria-labelledby={`${id}-label`} aria-describedby={describedBy(id, hint, error)}>
      <span className={s.label} id={`${id}-label`}>
        {label}
        {optional ? <span className={s.optional}> (optional)</span> : null}
        {selected.size > 0 ? <span className={s.muted}> · <span className="num">{selected.size}</span> chosen</span> : null}
      </span>
      {long ? (
        <div className={s.field}>
          <label className={s.filterLabel} htmlFor={`${id}-filter`}>Filter {label.toLowerCase()}</label>
          <span className={s.searchWrap}>
            <Search aria-hidden className={s.searchIcon} />
            <input id={`${id}-filter`} className={cx(s.input, s.searchInput)} type="search" placeholder={filterExample} autoComplete="off" value={filter} onChange={(e) => setFilter(e.target.value)} />
          </span>
        </div>
      ) : null}
      <div className={s.checklistBox}>
        {long && shown.length > 0 ? (
          <div className={s.checkTools}>
            <Button size="small" variant="link" disabled={unchosen === 0} onClick={() => onChange(new Set([...selected, ...shown.map((item) => item.id)]))}>
              {needle ? "Select all shown" : "Select all"} (<span className="num">{shown.length}</span>)
            </Button>
            <Button size="small" variant="link" disabled={selected.size === 0} onClick={() => onChange(new Set())}>Clear</Button>
          </div>
        ) : null}
        {shown.length === 0 ? <div className={s.checkEmpty}>{items.length === 0 ? emptyText : "Nothing matches the filter."}</div> : null}
        {shown.map((item) => (
          <label key={item.id} className={s.checkRow}>
            <input type="checkbox" className={s.checkbox} checked={selected.has(item.id)} onChange={() => toggle(item.id)} />
            <span className={s.checkText}>
              <span className={s.checkLabel}>{item.label}</span>
              {item.sub ? <span className={s.checkSub}>{item.sub}</span> : null}
            </span>
          </label>
        ))}
      </div>
      {hint ? <span className={s.hint} id={`${id}-hint`}>{hint}</span> : null}
      {error ? <FieldError id={id} error={error} /> : null}
    </div>
  );
}

/* ----------------------------------------------------------------- Meter */
const meterTone = { ok: s.meterOk, info: s.meterInfo, neutral: s.meterNeutral } as const;

export function Meter({ value, label, tone = "ok" }: { value: number; label: string; tone?: keyof typeof meterTone }) {
  return (
    <div className={s.meter} role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={value} aria-label={label}>
      <div className={cx(s.meterFill, meterTone[tone])} style={{ width: `${value}%` }} />
    </div>
  );
}

export { Logo } from "./Logo";
export { Avatar } from "./Avatar";
export { OrganisationBadge, OrganisationLogo } from "./OrganisationLogo";

export { ToastProvider, useNotify } from "./toast";
